<template>
    <admin-page :result="result" description="Plan, Stripe, vínculos y contadores de una cuenta">
        <kimsa-text-field class="search-field" @change="onSearch" :value="search" label="Buscar cuenta"
            placeholder="Nombre o email" prepend-inner-icon="mdi-magnify" dense hide-details />

        <div v-if="searching" class="detail-hint">Buscando...</div>

        <div v-else-if="results.length && !user" class="detail-results">
            <div v-for="hit in results" :key="hit.objectID" class="detail-row" @click="selectUser(hit)">
                <student-avatar :avatar="hit.avatar" :name="hit.name || hit.email" :avatar-key="hit.objectID"
                    :size="34" />
                <div class="detail-row-body">
                    <div class="detail-row-name">{{ hit.name || 'Sin nombre' }}</div>
                    <div class="detail-row-email">{{ hit.email }}</div>
                </div>
                <span class="detail-row-type">{{ accountTypeLabel(hit.accountType) }}</span>
            </div>
        </div>

        <div v-else-if="search && !searching && !user" class="detail-hint">Sin resultados.</div>

        <template v-if="user">
            <div class="detail-head">
                <student-avatar :avatar="user.avatar" :name="user.name || user.email" :avatar-key="user.id"
                    :size="52" />
                <div class="detail-head-body">
                    <div class="detail-head-name">{{ user.name || 'Sin nombre' }}</div>
                    <div class="detail-head-email">{{ user.email }}</div>
                </div>
                <button class="detail-clear" @click="clearUser">
                    <v-icon size="16">mdi-close</v-icon>
                </button>
            </div>

            <div v-for="section in sections" :key="section.name" class="detail-section">
                <div class="detail-section-title">{{ section.name }}</div>
                <div class="detail-fields">
                    <div v-for="field in section.fields" :key="field.label" class="detail-field">
                        <span>{{ field.label }}</span>
                        <b :class="field.warn ? 'error--text' : null">{{ field.value }}</b>
                    </div>
                </div>
            </div>

            <div class="detail-actions">
                <v-btn color="secondary" outlined class="detail-action" depressed :loading="confirming"
                    @click="confirmEmail">
                    <v-icon left size="18">mdi-email-check-outline</v-icon>
                    Confirmar email
                </v-btn>
                <v-btn v-if="!isStudent" color="secondary" outlined class="detail-action" depressed
                    @click="goTo('admin.user-access')">
                    <v-icon left size="18">mdi-key-outline</v-icon>
                    Accesos
                </v-btn>
                <v-btn color="secondary" outlined class="detail-action" depressed @click="goTo('admin.attach-users')">
                    <v-icon left size="18">mdi-account-multiple-plus-outline</v-icon>
                    Enlazar
                </v-btn>
                <v-btn v-if="canUnlink" color="error" outlined class="detail-action" depressed :loading="loading"
                    @click="$refs.confirmUnlink.show()">
                    <v-icon left size="18">mdi-link-variant-off</v-icon>
                    {{ unlinkLabel }}
                </v-btn>
                <v-btn v-if="isCompany" color="error" outlined class="detail-action" depressed
                    @click="goTo('admin.company-to-coach')">
                    <v-icon left size="18">mdi-office-building-remove-outline</v-icon>
                    Empresa a entrenador
                </v-btn>
                <v-btn color="error" outlined class="detail-action" depressed @click="goTo('admin.delete-user')">
                    <v-icon left size="18">mdi-account-remove-outline</v-icon>
                    Borrar usuario
                </v-btn>
            </div>

            <kimsa-confirm ref="confirmUnlink" alert-mode show-cancel :title="unlinkLabel" :content="unlinkContent"
                :persistent="false" :close-on-confirm="false" :loading="loading" @confirm="unlink" />
        </template>
    </admin-page>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/functions'
import KimsaTextField from '@/components/common/inputs/KimsaTextField.vue'
import KimsaConfirm from '@/components/common/KimsaConfirm.vue'
import StudentAvatar from '@/components/common/StudentAvatar.vue'
import AdminPage from '@/components/admin/AdminPage.vue'
import { UserIndex } from '@/utils/Algolia'
import { db } from '@/utils/Firebase'
import { adminRouteGuard } from '@/utils/Admin'

const ACCOUNT_TYPES = {
    student: 'Alumno',
    coach: 'Entrenador',
    coach_company: 'Entrenador de empresa',
    company: 'Empresa',
}

export default {
    name: 'AdminUserDetail',
    components: { KimsaTextField, KimsaConfirm, StudentAvatar, AdminPage },
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            search: '',
            results: [],
            user: null,
            studentsCount: 0,
            coachesCount: 0,
            searching: false,
            loading: false,
            confirming: false,
            result: null,
            searchTimer: null,
        }
    },
    computed: {
        isStudent(vm = this) {
            return vm.user?.accountType === 'student'
        },
        isCompany(vm = this) {
            return vm.user?.accountType === 'company'
        },
        isCoachCompany(vm = this) {
            return vm.user?.accountType === 'coach_company'
        },
        canUnlink(vm = this) {
            if (vm.isStudent) return !!vm.user?.studentSubscription?.coachId
            if (vm.isCoachCompany) return !!vm.user?.coachSubscription?.companyId
            return false
        },
        unlinkLabel(vm = this) {
            return vm.isStudent ? 'Desenlazar de su entrenador' : 'Desenlazar de su empresa'
        },
        unlinkContent(vm = this) {
            if (vm.isStudent)
                return `Vas a desenlazar a <b>${vm.user?.email}</b> de <b>${vm.user?.studentSubscription?.coachName || 'su entrenador'}</b>. Deja de ver sus rutinas y queda sin entrenador.`
            return `Vas a desenlazar a <b>${vm.user?.email}</b> de <b>${vm.user?.coachSubscription?.companyName || 'su empresa'}</b>. Pasa a ser entrenador independiente y tendrá que elegir un plan.`
        },
        sections(vm = this) {
            if (!vm.user) return []
            const user = vm.user
            const stripe = user.stripeAccount || {}
            const subscription = stripe.currentSubscription || {}
            const overrides = user.overrides || {}
            const counter = user.counter || {}

            const account = [
                { label: 'Tipo de cuenta', value: vm.accountTypeLabel(user.accountType) },
                { label: 'UID', value: user.id },
                { label: 'Teléfono', value: `${user.phoneCode || ''} ${user.phone || ''}`.trim() || '—' },
                { label: 'País', value: user.country?.name || user.country || '—' },
                { label: 'Creada', value: vm.formatDate(user.createdAt || user.created_at) },
                { label: 'Cuenta pausada', value: user.paused ? 'Sí' : 'No', warn: !!user.paused },
            ]

            const billing = [
                { label: 'Customer Stripe', value: stripe.id || '—' },
                { label: 'Suscripción', value: subscription.id || 'sin suscripción' },
                { label: 'Plan (priceId)', value: subscription.priceId || '—' },
                { label: 'Pago fallido', value: user.paymentFailed ? 'Sí' : 'No', warn: !!user.paymentFailed },
                { label: 'Acceso total', value: overrides.fullAccess ? 'Sí' : 'No' },
                { label: 'Plan forzado', value: overrides.priceId || '—' },
                { label: 'Tope de alumnos propio', value: overrides.customMaxClients || '—' },
            ]

            const links = []
            if (vm.isStudent) {
                links.push({ label: 'Entrenador', value: user.studentSubscription?.coachName || '—' })
                links.push({ label: 'Id del entrenador', value: user.studentSubscription?.coachId || '—' })
                links.push({ label: 'Suscripción pausada', value: user.studentSubscription?.paused ? 'Sí' : 'No' })
                links.push({ label: 'Equipo', value: user.team?.id || '—' })
                links.push({ label: 'Grupo', value: user.group || '—' })
            } else {
                if (vm.isCoachCompany) {
                    links.push({ label: 'Empresa', value: user.coachSubscription?.companyName || '—' })
                    links.push({ label: 'Id de la empresa', value: user.coachSubscription?.companyId || '—' })
                }
                links.push({ label: 'Alumnos activos', value: vm.studentsCount })
                if (user.accountType === 'company') links.push({ label: 'Entrenadores', value: vm.coachesCount })
            }

            const activity = [
                { label: 'Sesiones esta semana', value: counter.week ?? '—' },
                { label: 'Minutos esta semana', value: counter.durationWeek ?? '—' },
                { label: 'Mensajes sin leer', value: counter.messages ?? '—' },
                { label: 'Racha', value: counter.streak ?? '—' },
            ]

            const sections = [
                { name: 'Cuenta', fields: account },
                { name: 'Plan y cobro', fields: billing },
                { name: 'Vínculos', fields: links },
            ]
            if (vm.isStudent) sections.push({ name: 'Actividad', fields: activity })
            return sections
        },
    },
    async mounted() {
        const vm = this
        const email = vm.$route.query.email
        if (!email) return
        vm.search = email
        await vm.searchUsers()
        const hit = vm.results.find(user => user.email === email)
        if (hit) await vm.selectUser(hit)
    },
    methods: {
        accountTypeLabel(type) {
            return ACCOUNT_TYPES[type] || 'Desconocido'
        },
        formatDate(date) {
            if (!date) return '—'
            const value = date.toDate ? date.toDate() : date
            return this.$moment(value).isValid() ? this.$moment(value).format('DD/MM/YYYY') : '—'
        },
        goTo(route) {
            const vm = this
            vm.pushRoute({
                name: route,
                query: { email: vm.user?.email || '', type: vm.user?.accountType || '' },
            })
        },
        onSearch(value) {
            const vm = this
            vm.search = value
            vm.result = null
            if (vm.searchTimer) clearTimeout(vm.searchTimer)
            if (!value) {
                vm.results = []
                return
            }
            vm.searchTimer = setTimeout(vm.searchUsers, 350)
        },
        async searchUsers() {
            const vm = this
            await vm.withLoading(async () => {
                const { hits } = await UserIndex.search(vm.search, { hitsPerPage: 8 })
                vm.results = hits
            }, 'searching')
        },
        clearUser() {
            const vm = this
            vm.user = null
            vm.studentsCount = 0
            vm.coachesCount = 0
        },
        async selectUser(hit) {
            const vm = this
            vm.result = null
            await vm.withLoading(async () => {
                const doc = await db.collection('users').doc(hit.objectID).get()
                if (!doc.exists) {
                    vm.result = { type: 'error', message: 'La cuenta ya no existe en users.' }
                    return
                }
                vm.user = { id: doc.id, ...doc.data() }
                await vm.loadCounts()
            }, 'searching')
        },
        async loadCounts() {
            const vm = this
            vm.studentsCount = 0
            vm.coachesCount = 0
            if (vm.isStudent) return

            if (vm.user.accountType === 'company') {
                const coaches = await db.collection('users')
                    .where('coachSubscription.companyId', '==', vm.user.id).get()
                vm.coachesCount = coaches.size
                return
            }

            const students = await db.collection('users')
                .where('studentSubscription.coachId', '==', vm.user.id).get()
            vm.studentsCount = students.size
        },
        async confirmEmail() {
            const vm = this
            vm.result = null
            await vm.withLoading(async () => {
                try {
                    const fn = firebase.functions().httpsCallable('httpAdmin-confirmEmail')
                    const { data } = await fn({ email: vm.user.email })
                    vm.result = {
                        type: 'success',
                        message: data.alreadyVerified
                            ? `El email de ${vm.user.email} ya estaba verificado.`
                            : `Email de ${vm.user.email} marcado como verificado.`
                    }
                } catch (err) {
                    console.error('[adminUserDetail] confirmEmail', err)
                    vm.result = { type: 'error', message: err.message || 'Error al confirmar el email.' }
                }
            }, 'confirming')
        },
        async unlink() {
            const vm = this
            vm.loading = true
            vm.result = null
            try {
                const subscriptionId = vm.isStudent
                    ? vm.user.studentSubscription?.id
                    : vm.user.coachSubscription?.id

                if (subscriptionId) await db.collection('subscriptions').doc(subscriptionId).delete()

                const payload = vm.isStudent
                    ? { studentSubscription: null }
                    : { accountType: 'coach', coachSubscription: null }

                await db.collection('users').doc(vm.user.id).update(payload)
                vm.user = { ...vm.user, ...payload }
                vm.result = { type: 'success', message: `${vm.user.email} quedó desenlazado.` }
            } catch (err) {
                console.error('[adminUnlink] error', err)
                vm.result = { type: 'error', message: err.message || 'Error al desenlazar.' }
            } finally {
                vm.loading = false
                vm.$refs.confirmUnlink?.hide()
            }
        },
    },
}
</script>

<style scoped>
.search-field {
    max-width: 420px;
}

.detail-hint {
    margin-top: 14px;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}

.detail-results {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.detail-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    cursor: pointer;
}

.detail-row:hover {
    border-color: var(--v-accent-base);
}

.detail-row-body {
    flex: 1;
    min-width: 0;
}

.detail-row-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.detail-row-email {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.detail-row-type {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    white-space: nowrap;
}

.detail-head {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--v-outline-base);
}

.detail-head-body {
    flex: 1;
    min-width: 0;
}

.detail-head-name {
    font-size: 17px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
}

.detail-head-email {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.detail-clear {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    color: var(--v-txtMuted-base);
    border: 1px solid var(--v-outline2-base);
}

.detail-clear:hover {
    color: var(--v-txtPrimary-base);
    background: var(--v-surface2-base);
}

.detail-section {
    margin-top: 18px;
}

.detail-section-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--v-txtMuted-base);
    margin-bottom: 8px;
}

.detail-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 20px;
}

.detail-field {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
    border-bottom: 1px solid var(--v-outline-base);
    padding: 5px 0;
}

.detail-field b {
    color: var(--v-txtPrimary-base);
    font-weight: 600;
    text-align: right;
    word-break: break-all;
}

.detail-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 22px;
}

.detail-action {
    height: 38px !important;
    border-radius: 8px;
    text-transform: none;
    letter-spacing: normal;
}

@media (max-width: 600px) {
    .detail-fields {
        grid-template-columns: 1fr;
    }

    .detail-action {
        width: 100%;
    }
}
</style>
